import { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Upload, FileText, Search, Trash2, Download, Eye, BrainCircuit,
  Loader2, CheckCircle2, Clock, AlertCircle, File, Archive,
  ChevronRight, DollarSign, Info, ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { SavedViews } from '../components/SavedViews';
import { HelpPanel } from '../components/ui/help-panel';
import { documentInboxHelp } from '../lib/helpContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-200', icon: Clock },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Loader2 },
  ready_for_review: { label: 'Ready for Review', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Eye },
  approved: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle2 },
  filed: { label: 'Filed', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Archive },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle },
};

const formatCurrency = (amount) => amount ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount) : '-';
const formatSize = (bytes) => bytes < 1024 * 1024 ? `${(bytes / 1024).toFixed(0)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

export default function DocumentInbox() {
  const { currentWorkspace } = useWorkspace();
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const fileRef = useRef(null);
  const wsId = currentWorkspace?.workspace_id;

  // Sort state
  const [docSort, setDocSort] = useState('created_at');
  const [docSortDir, setDocSortDir] = useState('desc');
  const handleDocSort = (field) => {
    if (docSort === field) setDocSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setDocSort(field); setDocSortDir('desc'); }
  };
  const DocSortIcon = ({ field }) => {
    if (docSort !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return docSortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };

  const fetchAll = useCallback(async () => {
    if (!wsId) return;
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (search) params.append('search', search);
      const [docsRes, statsRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox?${params}`),
        apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/stats`),
      ]);
      if (docsRes.ok) { const d = await docsRes.json(); setDocuments(d.documents || []); }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [wsId, statusFilter, search]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 15 * 1024 * 1024) { toast.error(`${file.name} too large (max 15MB)`); continue; }
        const formData = new FormData();
        formData.append('file', file);
        const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/upload`, { method: 'POST', body: formData });
        if (res.ok) toast.success(`${file.name} uploaded`);
        else toast.error(`Failed to upload ${file.name}`);
      }
      if (fileRef.current) fileRef.current.value = '';
      fetchAll();
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleProcess = async (docId) => {
    setProcessing(docId);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${docId}/process`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success('Document processed successfully');
        fetchAll();
        // Open detail if it was the selected doc
        if (selectedDoc?.document_id === docId) {
          const updatedRes = await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${docId}`);
          if (updatedRes.ok) setSelectedDoc(await updatedRes.json());
        }
      } else { const e = await res.json(); toast.error(e.detail || 'Processing failed'); }
    } catch (e) { toast.error('Processing failed'); }
    finally { setProcessing(null); }
  };

  const updateStatus = async (docId, status) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${docId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    fetchAll();
    if (selectedDoc?.document_id === docId) setSelectedDoc(prev => ({ ...prev, status }));
    toast.success(`Status updated to ${STATUS_CONFIG[status]?.label}`);
  };

  const handleDelete = async (docId) => {
    await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${docId}`, { method: 'DELETE' });
    fetchAll(); setDetailOpen(false); toast.success('Document deleted');
  };

  const handleDownload = async (doc) => {
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${doc.document_id}/download`);
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = doc.filename; a.click(); URL.revokeObjectURL(url);
    }
  };

  const openDetail = async (doc) => {
    const res = await apiFetch(`${API_URL}/api/workspaces/${wsId}/document-inbox/${doc.document_id}`);
    if (res.ok) { setSelectedDoc(await res.json()); setDetailOpen(true); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-8 w-64" /><Skeleton className="h-32" /><Skeleton className="h-64" /></div>;

  return (
    <div className="space-y-6" data-testid="document-inbox">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Document Inbox</h1>
          <p className="text-muted-foreground text-sm">Upload documents for AI-powered extraction and processing</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            Upload Documents
          </Button>
          <HelpPanel {...documentInboxHelp} />
        </div>
        <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} accept=".pdf,.png,.jpg,.jpeg,.xlsx,.csv" multiple />
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'border-l-gray-400', tip: 'Total documents in your inbox across all statuses' },
            { label: 'Pending', value: stats.pending, color: 'border-l-gray-400', tip: 'Documents uploaded but not yet processed by AI' },
            { label: 'Processing', value: stats.processing, color: 'border-l-blue-500', tip: 'Documents currently being analyzed by AI' },
            { label: 'Review', value: stats.ready_for_review, color: 'border-l-amber-500', tip: 'AI extraction complete — review the extracted data and approve or file' },
            { label: 'Approved', value: stats.approved, color: 'border-l-emerald-500', tip: 'Reviewed and approved documents ready to be filed or actioned' },
            { label: 'Filed', value: stats.filed, color: 'border-l-purple-500', tip: 'Archived documents — fully processed and stored' },
          ].map(s => (
            <TooltipProvider key={s.label}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Card className={cn("border-l-4 cursor-help", s.color)}>
                    <CardContent className="p-2.5 text-center">
                      <p className="text-xl font-bold">{s.value}</p>
                      <div className="flex items-center justify-center gap-1">
                        <p className="text-[10px] text-muted-foreground">{s.label}</p>
                        <Info className="w-2.5 h-2.5 text-muted-foreground/40" />
                      </div>
                    </CardContent>
                  </Card>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs max-w-[200px]">{s.tip}</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      )}

      {/* Upload Drop Zone */}
      <Card className="border-dashed border-2 hover:border-emerald-300 dark:hover:border-emerald-700 transition-colors cursor-pointer" onClick={() => fileRef.current?.click()}>
        <CardContent className="p-8 text-center">
          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
          <p className="font-medium">Drop files here or click to upload</p>
          <p className="text-xs text-muted-foreground mt-1">PDFs, images, spreadsheets up to 15MB</p>
        </CardContent>
      </Card>

      {/* Saved Views + Filters */}
      <div className="space-y-3">
        <SavedViews
          page="documents"
          presets={[
            { name: 'Needs Review', icon: 'eye', filters: { status: 'ready_for_review' }, sort: { field: 'created_at', dir: 'desc' } },
            { name: 'Pending', icon: 'clock', filters: { status: 'pending' }, sort: { field: 'created_at', dir: 'desc' } },
            { name: 'Highest Amount', icon: 'dollar', filters: { status: 'all' }, sort: { field: 'extracted_amount', dir: 'desc' } },
            { name: 'Recently Filed', icon: 'file', filters: { status: 'filed' }, sort: { field: 'created_at', dir: 'desc' } },
          ]}
          onApply={({ filters, sort }) => {
            if (filters.status) setStatusFilter(filters.status);
            if (filters.search) setSearch(filters.search);
            if (sort.field) { setDocSort(sort.field); setDocSortDir(sort.dir || 'desc'); }
          }}
          currentFilters={{ status: statusFilter, search }}
          currentSort={{ field: docSort, dir: docSortDir }}
        />
        <div className="flex gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search documents..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="inbox-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="inbox-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* Document Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30">
                <TableHead className="cursor-pointer select-none" onClick={() => handleDocSort('filename')}><span className="flex items-center gap-1">Document <DocSortIcon field="filename" /></span></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleDocSort('status')}><span className="flex items-center gap-1">Status <DocSortIcon field="status" /></span></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleDocSort('extracted_vendor')}><span className="flex items-center gap-1">Vendor <DocSortIcon field="extracted_vendor" /></span></TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => handleDocSort('extracted_amount')}><span className="flex items-center gap-1 justify-end">Amount <DocSortIcon field="extracted_amount" /></span></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleDocSort('extracted_date')}><span className="flex items-center gap-1">Date <DocSortIcon field="extracted_date" /></span></TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.length > 0 ? [...documents].sort((a, b) => {
                let valA = a[docSort], valB = b[docSort];
                if (typeof valA === 'string' || valA == null) { valA = (valA || '').toLowerCase(); valB = (valB || '').toLowerCase(); }
                else { valA = valA || 0; valB = valB || 0; }
                if (valA < valB) return docSortDir === 'asc' ? -1 : 1;
                if (valA > valB) return docSortDir === 'asc' ? 1 : -1;
                return 0;
              }).map(doc => {
                const st = STATUS_CONFIG[doc.status] || STATUS_CONFIG.pending;
                const StIcon = st.icon;
                return (
                  <TableRow key={doc.document_id} className="cursor-pointer hover:bg-muted/30" onClick={() => openDetail(doc)} data-testid={`doc-${doc.document_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <File className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <div>
                          <p className="font-medium text-sm truncate max-w-[200px]">{doc.filename}</p>
                          <p className="text-[10px] text-muted-foreground">{formatSize(doc.size)} - {new Date(doc.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn("text-[10px] gap-1", st.color)}>
                        <StIcon className={cn("w-3 h-3", doc.status === 'processing' && "animate-spin")} />
                        {st.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{doc.extracted_vendor || '-'}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px] capitalize">{doc.extracted_type || '-'}</Badge></TableCell>
                    <TableCell className="text-right font-mono text-sm">{formatCurrency(doc.extracted_amount)}</TableCell>
                    <TableCell className="text-sm">{doc.extracted_date || '-'}</TableCell>
                    <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1 justify-end">
                        {doc.status === 'pending' && (
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleProcess(doc.document_id); }} disabled={processing === doc.document_id} data-testid={`process-${doc.document_id}`}>
                            {processing === doc.document_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <BrainCircuit className="w-3 h-3 mr-1" />}
                            Process
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}><Download className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={(e) => { e.stopPropagation(); handleDelete(doc.document_id); }}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow><TableCell colSpan={7} className="text-center py-8">
                  <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No documents in inbox</p>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Document Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="doc-detail-dialog">
          {selectedDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <File className="w-5 h-5" />
                  {selectedDoc.filename}
                </DialogTitle>
                <DialogDescription>
                  Uploaded {new Date(selectedDoc.created_at).toLocaleString()} by {selectedDoc.uploaded_by_name}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {/* Status Bar */}
                <div className="flex items-center gap-2">
                  <Badge className={cn("text-xs", STATUS_CONFIG[selectedDoc.status]?.color)}>{STATUS_CONFIG[selectedDoc.status]?.label}</Badge>
                  {selectedDoc.status === 'pending' && (
                    <Button size="sm" onClick={() => handleProcess(selectedDoc.document_id)} disabled={processing === selectedDoc.document_id} data-testid="detail-process-btn">
                      {processing === selectedDoc.document_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <BrainCircuit className="w-3 h-3 mr-1" />}
                      Process with AI
                      <span className="text-[9px] ml-1 opacity-60">(uses credits)</span>
                    </Button>
                  )}
                </div>

                {/* Extracted Data */}
                {selectedDoc.extracted_vendor && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] text-muted-foreground uppercase">Vendor</p>
                      <p className="font-medium text-sm">{selectedDoc.extracted_vendor}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] text-muted-foreground uppercase">Amount</p>
                      <p className="font-medium text-sm">{formatCurrency(selectedDoc.extracted_amount)}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] text-muted-foreground uppercase">Date</p>
                      <p className="font-medium text-sm">{selectedDoc.extracted_date || '-'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/50">
                      <p className="text-[10px] text-muted-foreground uppercase">Type</p>
                      <p className="font-medium text-sm capitalize">{selectedDoc.extracted_type || '-'}</p>
                    </div>
                  </div>
                )}

                {selectedDoc.extracted_description && (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-[10px] text-muted-foreground uppercase">Description</p>
                    <p className="text-sm">{selectedDoc.extracted_description}</p>
                  </div>
                )}

                {/* Line Items */}
                {selectedDoc.extracted_line_items?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Line Items</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-muted/30">
                          <TableHead className="text-xs">Description</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Price</TableHead>
                          <TableHead className="text-xs text-right">Amount</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedDoc.extracted_line_items.map((li, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-xs">{li.description}</TableCell>
                            <TableCell className="text-xs text-right">{li.quantity}</TableCell>
                            <TableCell className="text-xs text-right">{formatCurrency(li.unit_price)}</TableCell>
                            <TableCell className="text-xs text-right font-medium">{formatCurrency(li.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Suggested Accounts */}
                {selectedDoc.suggested_accounts?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Suggested Accounts</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedDoc.suggested_accounts.map(code => (
                        <Badge key={code} variant="outline" className="text-xs">{code}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selectedDoc.processing_error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                    <p className="text-xs text-red-600 font-medium">Processing Error</p>
                    <p className="text-xs text-red-500 mt-1">{selectedDoc.processing_error}</p>
                  </div>
                )}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" onClick={() => handleDownload(selectedDoc)}><Download className="w-4 h-4 mr-1" />Download</Button>
                {selectedDoc.status === 'ready_for_review' && (
                  <>
                    <Button onClick={() => updateStatus(selectedDoc.document_id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                      <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                    </Button>
                    <Button variant="outline" onClick={() => updateStatus(selectedDoc.document_id, 'filed')}><Archive className="w-4 h-4 mr-1" />File</Button>
                  </>
                )}
                {selectedDoc.status === 'approved' && (
                  <Button variant="outline" onClick={() => updateStatus(selectedDoc.document_id, 'filed')}><Archive className="w-4 h-4 mr-1" />Move to Filed</Button>
                )}
                <Button variant="ghost" className="text-destructive" onClick={() => handleDelete(selectedDoc.document_id)}><Trash2 className="w-4 h-4 mr-1" />Delete</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
